// ACL-09 (#611, LEAK-05, D-22, RESEARCH Pitfall 9) — keep the SPA from sending
// a paid AI request for an employee without inventory.ai.generate. The 09-07
// server gate answers such a request with 403; these guards make that 403 a
// backstop instead of a mid-flow error.
//
// A holder gets the original object back (same identity, behaviour unchanged).
// A non-holder gets a copy whose `mutate` does nothing and whose `mutateAsync`
// rejects with a PaidAiPermissionError, so no request is ever issued.
//
// #1458 (WR-04): a permission denial is its own outcome, never the
// "AI isn't enabled for this dealership" (skipped_ai_disabled) one.

interface MutationLike {
  mutate: (...args: never[]) => void;
  mutateAsync: (...args: never[]) => Promise<unknown>;
}

const noop = () => {};

/** Client-only status for a paid AI action the employee may not run. */
export const AI_PERMISSION_DENIED_STATUS = 'skipped_no_permission' as const;

/** Toast copy for a paid AI action the employee may not run. */
export const AI_PERMISSION_DENIED_MESSAGE = "You don't have permission to run AI actions.";

/** The rejection of a guarded `mutateAsync` (inventory.ai.generate missing). */
export class PaidAiPermissionError extends Error {
  readonly permission = 'inventory.ai.generate';

  constructor() {
    super(AI_PERMISSION_DENIED_MESSAGE);
    this.name = 'PaidAiPermissionError';
  }
}

export function isPaidAiPermissionError(error: unknown): error is PaidAiPermissionError {
  return error instanceof PaidAiPermissionError;
}

function rejectWithoutPermission(): Promise<never> {
  const rejection = Promise.reject(new PaidAiPermissionError());
  // Pre-handled: a fire-and-forget caller never raises an unhandled rejection,
  // while an awaiting caller still receives the PaidAiPermissionError.
  rejection.catch(noop);
  return rejection;
}

/** A TanStack mutation that cannot fire unless `allowed`. */
export function guardPaidMutation<M extends MutationLike>(mutation: M, allowed: boolean): M {
  if (allowed) {
    return mutation;
  }
  return { ...mutation, mutate: noop, mutateAsync: rejectWithoutPermission };
}

/** An AI operation hook result (`{ trigger, … }`) whose trigger cannot fire unless `allowed`. */
export function guardPaidOperation<O extends { trigger: MutationLike }>(operation: O, allowed: boolean): O {
  if (allowed) {
    return operation;
  }
  return { ...operation, trigger: guardPaidMutation(operation.trigger, false) };
}
